import { useEffect, useMemo } from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { CREATE_PLAYLIST_ADD_LESSON_PARAM, CREATE_PLAYLIST_ADD_TRACK_PARAM, CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, MY_SAVED_ITEMS_PAGE_SIZE, useArchivedPlaylists, useLatestSavedItemsInteractions, useMyPlaylists, usePlaylist, useSavePlaylist, useSetHiddenForOwner, useUnsavePlaylist } from '.';
import { CREATE_PLAYLIST_URL_FRAGMENT, EDIT_PLAYLIST_URL_FRAGMENT, PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT } from '../../constants/Navigation';
import { FILTER_CONSTS, FILTER_TYPES } from '../../constants/TopFilterConstants'; // @ts-expect-error Could not find a declaration file for module

import { useSearchParam } from '../../hooks/searchParamHooks';
import { removeTrailingSlash } from '../../utils/RoutingUtils';
import { useMediaQuery } from '../../utils/Styling';
import { useUserInfo } from '../accounts';
import { useLesson } from '../lessons';
import { useTrack } from '../tracks';
import { usePlaylistProgress } from '../userProgress';
export const useMyPlaylistsParams = () => {
  const {
    page
  } = useParams();
  return {
    page
  };
};
export const usePlaylistsFilters = () => {
  const [[selectedCreatedBy]] = useSearchParam(FILTER_TYPES.CREATED_BY);
  const [[searchFilter]] = useSearchParam(FILTER_TYPES.SEARCH);
  return {
    selectedCreatedBy,
    searchFilter
  };
};
export const useFilteredMyPlaylists = () => {
  const {
    page: pageParam
  } = useMyPlaylistsParams();
  const {
    selectedCreatedBy,
    searchFilter
  } = usePlaylistsFilters();
  const includeSaved = selectedCreatedBy !== FILTER_CONSTS.CREATED_BY.ME;
  const archivedOnly = selectedCreatedBy === FILTER_CONSTS.CREATED_BY.ARCHIVED;
  const page = Number(pageParam) || 1;
  const hasFilters = !!selectedCreatedBy || !!searchFilter;
  const {
    totalPlaylists,
    playlists,
    playlistsLoading,
    playlistsError
  } = useMyPlaylists({
    limit: MY_SAVED_ITEMS_PAGE_SIZE,
    page,
    includeSaved,
    skip: archivedOnly
  });
  const {
    totalArchivedPlaylists,
    archivedPlaylists,
    archivedPlaylistsLoading,
    archivedPlaylistsError
  } = useArchivedPlaylists({
    limit: MY_SAVED_ITEMS_PAGE_SIZE,
    page,
    includeSaved,
    skip: !archivedOnly
  });
  return {
    totalPlaylists: totalPlaylists || totalArchivedPlaylists,
    page,
    hasFilters,
    playlists: playlists || archivedPlaylists,
    playlistsLoading: playlistsLoading || archivedPlaylistsLoading,
    playlistsError: playlistsError || archivedPlaylistsError,
    selectedCreatedBy,
    searchFilter
  };
}; // centralises the logic for playlists for both scenarios (owned and other)

export const usePlaylistSaveStatus = (playlistId, {
  showNotification,
  language
} = {}) => {
  const {
    playlist,
    playlistLoading
  } = usePlaylist(playlistId);
  const {
    userInfo,
    userInfoLoading
  } = useUserInfo();
  const isOwner = userInfo && playlist && userInfo.user.id === playlist.ownerUserId;
  const isSaved = playlist && (isOwner ? !playlist.hiddenForOwner : playlist.savedByUser);
  const [savePlaylist] = useSavePlaylist(playlistId, {
    showNotification,
    language
  });
  const [unsavePlaylist] = useUnsavePlaylist(playlistId, {
    showNotification,
    language
  });
  const [toggleHiddenForOwner] = useSetHiddenForOwner(playlistId, playlist !== null && !playlist.hiddenForOwner, {
    showNotification,
    language
  });

  const togglePlaylistSaveStatus = () => {
    if (isOwner) return toggleHiddenForOwner();else if (isSaved) return unsavePlaylist();else return savePlaylist();
  };

  return {
    isSaved,
    isOwner,
    isLoading: playlistLoading || userInfoLoading,
    togglePlaylistSaveStatus
  };
};
export const useCloseCreatePlaylistModalPath = () => {
  const location = useLocation();
  return useMemo(() => {
    const {
      pathname: currentPathname
    } = location;
    const createPlaylistUrlFragmentIndex = currentPathname.indexOf(CREATE_PLAYLIST_URL_FRAGMENT);
    const editPlaylistUrlFragmentIndex = currentPathname.indexOf(EDIT_PLAYLIST_URL_FRAGMENT);
    let originalUrl = currentPathname;

    if (createPlaylistUrlFragmentIndex !== -1) {
      originalUrl = currentPathname.substring(0, createPlaylistUrlFragmentIndex);
    } else if (editPlaylistUrlFragmentIndex !== -1) {
      originalUrl = currentPathname.substring(0, editPlaylistUrlFragmentIndex);
    }

    const originalParams = new URLSearchParams(location.search);
    originalParams.delete(CREATE_PLAYLIST_ADD_TRACK_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ADD_LESSON_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM);
    return `${removeTrailingSlash(originalUrl) || '/'}${originalParams.size ? `?${originalParams}` : ''}`;
  }, [location]);
};
export const useCreatePlaylistModalPath = ({
  trackId,
  lessonId,
  language
} = {}) => {
  const location = useLocation();
  return useMemo(() => {
    let sourceUrl = removeTrailingSlash(location.pathname);
    if (sourceUrl === '/') sourceUrl = '';
    const params = new URLSearchParams(location.search);
    if (trackId) params.append(CREATE_PLAYLIST_ADD_TRACK_PARAM, trackId.toString());
    if (lessonId) params.append(CREATE_PLAYLIST_ADD_LESSON_PARAM, lessonId.toString());
    if (language) params.append(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, language);
    return `${sourceUrl}${CREATE_PLAYLIST_URL_FRAGMENT}${params.size ? `?${params}` : ''}`;
  }, [language, lessonId, location.pathname, location.search, trackId]);
};
export const useSavedItemsTooltip = () => {
  const {
    latestInteraction
  } = useLatestSavedItemsInteractions();
  const {
    itemId,
    playlistId,
    type,
    action,
    language
  } = latestInteraction || {};
  const {
    playlist,
    playlistLoading
  } = usePlaylist(playlistId || -1, !latestInteraction || !playlistId);
  const {
    track,
    trackLoading
  } = useTrack(itemId || -1, language, type !== 'TRACK');
  const {
    lesson,
    lessonLoading
  } = useLesson(itemId || -1, language, type !== 'LESSON');
  const isLoading = playlistLoading || trackLoading || lessonLoading;
  const show = !!latestInteraction && !isLoading;
  const itemName = track ? track.name : lesson ? lesson.name : '';
  const playlistName = playlist && playlist.name;
  return {
    show,
    action,
    itemName,
    itemId,
    itemType: type,
    playlistName,
    playlistId
  };
};
export const useShowNavbarWatchLaterTooltip = () => {
  const {
    show,
    action
  } = useSavedItemsTooltip();
  return show && action && ['ADD_TO_WATCH_LATER', 'REMOVE_FROM_WATCH_LATER'].includes(action);
};
export const useShowNavbarPlaylistsTooltip = () => {
  const {
    show,
    action
  } = useSavedItemsTooltip();
  return show && action && ['SAVE_PLAYLIST', 'UNSAVE_PLAYLIST', 'ARCHIVE_PLAYLIST', 'UNARCHIVE_PLAYLIST', 'DUPLICATE_PLAYLIST', 'ADD_TO_PLAYLIST', 'REMOVE_FROM_PLAYLIST'].includes(action);
};
export const useTimedLatestSavedItemsInteraction = bypass => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    latestInteraction,
    removeLatestInteraction
  } = useLatestSavedItemsInteractions();
  useEffect(() => {
    if (isMobile || latestInteraction && !bypass) {
      const timeout = setTimeout(() => {
        removeLatestInteraction();
      }, 5000);
      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [latestInteraction, removeLatestInteraction, isMobile, bypass]);
  return latestInteraction ? latestInteraction.action : null;
};
export const useAddToPlaylistOptionList = ({
  page = 1,
  skip = false
}) => useMyPlaylists({
  limit: MY_SAVED_ITEMS_PAGE_SIZE,
  page,
  includeSaved: false,
  skip
});
export const usePlaylistRemainingItemsAndProgress = playlistId => {
  const {
    playlistProgress,
    playlistProgressLoading
  } = usePlaylistProgress(playlistId);
  const status = playlistProgress ? playlistProgress.status : null;
  const remainingProgressItems = playlistProgress ? playlistProgress.itemsProgress.filter(item => item.status !== 'COMPLETED') : [];
  const remainingProgressItemCount = remainingProgressItems.length;
  const totalProgressItemCount = playlistProgress ? playlistProgress.itemsProgress.length : 0;
  const progressAmount = status === FILTER_CONSTS.STATUSES.COMPLETED ? 1 : status === FILTER_CONSTS.STATUSES.IN_PROGRESS ? (totalProgressItemCount - remainingProgressItemCount) / totalProgressItemCount : 0;
  return {
    remainingProgressItems,
    remainingProgressItemCount,
    totalProgressItemCount,
    progressAmount,
    status,
    playlistProgressLoading
  };
};
export const useIsPlaylistRoute = () => {
  const match = useRouteMatch([PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT]);
  return match ? match.isExact : false;
};